"use client";
import React, { useRef, useEffect, useState } from "react";
import Image from "next/image";
import HeadingSize from "@/components/ui/HeadingSize";
import { useTranslation } from "react-i18next";

export default function DoctorsTestimonialsSectionMobile() {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

  const doctors = [
    {
      name: t("home.doctorTestimonials.drRicha.name"),
      title: t("home.doctorTestimonials.drRicha.title"),
      image: "/images/Dr. Images/richa.webp",
      quote: t("home.doctorTestimonials.drRicha.quote"),
    },
    {
      name: t("home.doctorTestimonials.ektaKhurana.name"),
      title: t("home.doctorTestimonials.ektaKhurana.title"),
      image: "/images/Dr. Images/ekta-khuranna.webp",
      quote: t("home.doctorTestimonials.ektaKhurana.quote"),
    },
    {
      name: t("home.doctorTestimonials.abhinavMahajan.name"),
      title: t("home.doctorTestimonials.abhinavMahajan.title"),
      image: "/images/Dr. Images/Abhinav.webp",
      quote: t("home.doctorTestimonials.abhinavMahajan.quote"),
    },
  ];

  const loopedDoctors = [...doctors, ...doctors, ...doctors];
  const [currentIndex, setCurrentIndex] = useState(doctors.length);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const cardWidth = container.offsetWidth * 0.75; // Match width below
      container.style.scrollBehavior = "auto";
      container.scrollLeft = cardWidth * doctors.length;
      setTimeout(() => {
        if (container) container.style.scrollBehavior = "smooth";
      }, 100);
    }
  }, [doctors.length]);

  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;

    const cardWidth = container.offsetWidth * 0.75;
    const scrollPos = container.scrollLeft;
    const index = Math.round(scrollPos / cardWidth);
    setCurrentIndex(index % doctors.length);

    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    scrollTimeout.current = setTimeout(() => {
      if (index <= doctors.length - 1) {
        container.style.scrollBehavior = "auto";
        container.scrollLeft = cardWidth * (index + doctors.length);
        setTimeout(() => {
          if (container) container.style.scrollBehavior = "smooth";
        }, 50);
      } else if (index >= doctors.length * 2) {
        container.style.scrollBehavior = "auto";
        container.scrollLeft = cardWidth * (index - doctors.length);
        setTimeout(() => {
          if (container) container.style.scrollBehavior = "smooth";
        }, 50);
      }
    }, 150);
  };

  const doctor = doctors[currentIndex % doctors.length];

  return (
    <section className="lg:hidden py-4 bg-white">
      <div className=" text-center mb-8">
        <HeadingSize text={t("home.trustedFacesLine1")} />
        <HeadingSize text={t("home.trustedFacesLine2")} />
      </div>

      {/* Scrollable Images */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory"
      >
        {loopedDoctors.map((doc, index) => {
          const isCurrent =
            index % doctors.length === currentIndex % doctors.length;
          return (
            <div
              key={`${doc.name}-${index}`}
              className={`snap-center flex-shrink-0 transition-all duration-300 ${
                isCurrent ? "scale-100" : "scale-90 opacity-70"
              }`}
              style={{ width: "75%", paddingRight: "10px" }}
            >
              <div className="relative w-full">
                <Image
                  src={doc.image}
                  alt={doc.name}
                  width={500}
                  height={600}
                  className="w-full h-auto object-cover md:w-[400px] md:h-[500px]"
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Quote */}
      <div className="w-full max-w-[280px] sm:max-w-[300px] text-left mx-auto my-8">
        <p className="text-lg md:text-xl text-black font-bold">
          "{doctor?.quote}"
        </p>
      </div>
    </section>
  );
}
